import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../api';
import Topbar from '../../Tools/Topbar';
import LeftSidebar from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import PatientNewCaseModal from '../EMSCasesTab/CaseDetails/PatientSituationModal';

// Date/Time Format Fetcher
const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axiosInstance.get('/ems/get-system-setting');
        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const data = res.data.data[0];
          sessionStorage.setItem('date_format', data.date_format || 'DD/MM/YYYY');
          sessionStorage.setItem('time_format', data.time_format || '24');
        }
      } catch (err) {
        console.error('Error fetching date/time formats:', err);
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []);
  return null;
};

const HospitalDetailTab = () => {
  const navigate = useNavigate();

  const [caseId] = useState(sessionStorage.getItem('currentCaseId'));
  const [caseData, setCaseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [showAcknowledgeButton, setShowAcknowledgeButton] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [userRole] = useState(sessionStorage.getItem('Role_Name') || '');
  const [userId] = useState(sessionStorage.getItem('user_id') || '');

  const settingsRef = useRef();
  const patientModalRef = useRef();
  const pollIntervalRef = useRef(null);

  const { settings, updateSetting, handleColorTheme } = useSettings();

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  // Format date
  const formatCaseDate = (dateString) => {
    if (!dateString) return 'N/A';
  
    try {
      const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
      const timeFormat = sessionStorage.getItem('time_format') || '24';
  
      // Map your backend formats to date-fns
      const dateFormatMap = {
        'DD/MM/YYYY': 'dd/MM/yyyy',
        'MM/DD/YYYY': 'MM/dd/yyyy',
        'YYYY-MM-DD': 'yyyy-MM-dd',
      };
  
      const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
      const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;
  
      let utcDate;
  
      if (dateString.includes('T')) {
        // ISO format: 2025-10-27T16:27:19.000000Z
        utcDate = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
      } else {
        // MySQL format: 2025-10-27 16:27:19
        utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // Force UTC
      }
  
      // Extract UTC parts manually to avoid local timezone shift
      const formatted = format(
        new Date(
          utcDate.getUTCFullYear(),
          utcDate.getUTCMonth(),
          utcDate.getUTCDate(),
          utcDate.getUTCHours(),
          utcDate.getUTCMinutes(),
          utcDate.getUTCSeconds()
        ),
        formatString
      );
  
      return formatted;
    } catch (err) {
      console.error('Error formatting alarm time:', err, dateString);
      return 'Invalid Date';
    }
  };

  // Fetch case details
  const fetchCaseDetails = async () => {
    if (!caseId) {
      setError('No case selected');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const res = await axiosInstance.get(`/ems/cases/${caseId}`);
      const data = res.data.data || res.data;
      setCaseData(data);
    } catch (err) {
      console.error('Error fetching case:', err);
      setError(err.response?.data?.message || 'Failed to load case details');
    } finally {
      setIsLoading(false);
    }
  };

  // Check acknowledge button
  const checkAcknowledgeButton = async () => {
    if (!caseId || !userId || userRole !== 'Hospital Representative (Nurse)') {
      setShowAcknowledgeButton(false);
      return;
    }

    try {
      const res = await axiosInstance.get(`/ems/show-case-acknowledgement/${caseId}/${userId}`);
      setShowAcknowledgeButton(res.data.show_button === true);
    } catch (err) {
      setShowAcknowledgeButton(false);
    }
  };

  // Acknowledge case
  const handleAcknowledge = async () => {
    if (!caseId || !sessionStorage.getItem('auth_token')) {
      navigate('/login');
      return;
    }

    try {
      setIsAcknowledging(true);
      await axiosInstance.get(`/ems/case-acknowledgement/${caseId}`);
      alert('Case acknowledged successfully');
      await fetchCaseDetails();
      setShowAcknowledgeButton(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to acknowledge case');
    } finally {
      setIsAcknowledging(false);
    }
  };

  // Documents
  const getDocuments = () => {
    if (!caseData) return [];
    return [caseData.documents1, caseData.documents2, caseData.documents3]
      .filter(doc => doc && doc.trim() !== '');
  };

  const handleDocumentClick = (url) => {
    setSelectedDocument(url);
    setShowDocumentModal(true);
  };

  const renderDocumentContent = (url) => {
    const ext = url.split('.').pop().toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      return <img src={url} alt="Document" className="img-fluid" style={{ maxHeight: '80vh' }} />;
    }
    if (['pdf', 'xml'].includes(ext)) {
      return <iframe src={url} width="100%" height="600px" title="Document" />;
    }
    return <p>Unsupported file format: {ext}</p>;
  };

  // Patient modal
  const handleViewPatientSituation = () => {
    sessionStorage.setItem('currentCaseId', caseId);
    setIsPatientModalOpen(true);
  };

  const handlePatientSituationSave = () => {
    setIsPatientModalOpen(false);
    fetchCaseDetails();
  };

  // Effects
  useEffect(() => {
    fetchCaseDetails();
  }, [caseId]);

  useEffect(() => {
    checkAcknowledgeButton();
    pollIntervalRef.current = setInterval(checkAcknowledgeButton, 5000);
    return () => clearInterval(pollIntervalRef.current);
  }, [caseId, userId, userRole]);

  const documents = getDocuments();

  return (
    <div id="main-wrapper">
      <DateTimeFormatFetcher />

      {/* Always render layout — critical for Topbar hamburger */}
      <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <Topbar toggleSidebar={toggleSidebar} />

        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings
              ref={settingsRef}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
              settings={settings}
            />

            {/* Show loading spinner inside content, not replace layout */}
            {isLoading && (
              <div className="d-flex justify-content-center align-items-center py-5 my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading case...</span>
                </div>
              </div>
            )}

            {error && <div className="alert alert-danger m-4">{error}</div>}

            {!isLoading && !error && !caseData && (
              <div className="alert alert-warning m-4">No case data found</div>
            )}

            {!isLoading && caseData && (
              <div className="card w-100 position-relative overflow-hidden">
                <div className="px-4 py-3 border-bottom">
                  <h5 className="card-title fw-semibold mb-0">Case Detail</h5>
                  <p className="card-subtitle mb-0 mt-1">Comprehensive case overview</p>
                </div>

                <div className="card-body p-4">
                  <div className="table-responsive rounded-2">
                    <table className="table text-nowrap mb-0 align-middle">
                      <thead className="text-dark fs-4">
                        <tr>
                          <th><h6 className="fw-semibold mb-0">Field</h6></th>
                          <th><h6 className="fw-semibold mb-0">Value</h6></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Case ID</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.id}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Name</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.case_name}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Location</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.location?.location_name || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Ambulance</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.ambulance?.plate_number || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Medical Center</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.medical_center?.name || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Paramedics ID</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.ems_member || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Paramedics Name</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.ems_member_name || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Paramedics Status</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.ems_member?.status ? 'Busy' : 'Available'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Patient Location</p></td>
                          <td><p className="mb-0 fw-normal">
                            {caseData?.p_location?.location_name || 'N/A'}
                          </p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Patient Area</p></td>
                          <td><p className="mb-0 fw-normal">
                            {caseData?.p_area?.area_name || 'N/A'}
                          </p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Accident Place</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.accident_place || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Gender</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.gender || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Age</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.age || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Bracelet ID</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.bracelet_id || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Type of Injury</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.injury_type || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Contact Number</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.contact_number || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Triage Card</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.triage_card || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Acknowledgment</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.acknowledgment || 'Pending'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Documents</p></td>
                          <td>
                            <p className="mb-0 fw-normal">
                              <i className="ti ti-paperclip me-2"></i>
                              {documents.length > 0 ? (
                                documents.map((doc, index) => (
                                  <button
                                    key={index}
                                    type="button"
                                    className="btn btn-link me-3 p-0"
                                    onClick={() => handleDocumentClick(doc)}
                                    title={`Document ${index + 1}`}
                                  >
                                    <i className="ti ti-photo"></i>
                                  </button>
                                ))
                              ) : (
                                'No documents'
                              )}
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Case Date</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.created_at ? formatCaseDate(caseData.created_at) : 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold mb-0">Patient Situation</p></td>
                          <td>
                            <p className="mb-0 fw-normal">
                              <a
                                data-bs-toggle="modal"
                                data-bs-target="#bs-fullscreen"
                                className="text-primary"
                                onClick={handleViewPatientSituation}
                              >
                                Click to view details
                              </a>
                            </p>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Acknowledge Button */}
                    {showAcknowledgeButton && userRole === 'Hospital Representative (Nurse)' && (
                      <div className="mt-4">
                        <button
                          className="btn btn-success rounded-pill px-4"
                          onClick={handleAcknowledge}
                          disabled={isAcknowledging}
                        >
                          {isAcknowledging ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Acknowledging...
                            </>
                          ) : (
                            'Acknowledge'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Document Modal */}
            {showDocumentModal && (
              <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Document Viewer</h5>
                      <button type="button" className="btn-close" onClick={() => setShowDocumentModal(false)}></button>
                    </div>
                    <div className="modal-body text-center">
                      {selectedDocument && renderDocumentContent(selectedDocument)}
                    </div>
                    <div className="modal-footer">
                      <button className="btn btn-secondary" onClick={() => setShowDocumentModal(false)}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Patient Modal */}
            <PatientNewCaseModal
              ref={patientModalRef}
              isOpen={isPatientModalOpen}
              onClose={() => setIsPatientModalOpen(false)}
              caseId={caseId}
              onSave={handlePatientSituationSave}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HospitalDetailTab;